import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowDown, ArrowUp, Banknote, Building, Handshake, TrendingUp, TrendingDown, Minus, GitCompare } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { ExportButtons } from '@/components/ExportButtons';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { localToday, toLocalYmd } from '@/lib/dateRange';
import { GlAccountingStrip } from '@/components/accounting/GlAccountingStrip';
import { ReportHeader } from '@/components/reports/ReportUI';

const FlowRow = ({ label, amount, indent = false, bold = false }) => (
  <div className={`flex justify-between py-1.5 ${indent ? 'pl-6' : ''} ${bold ? 'font-bold border-t pt-2 mt-1' : ''}`}>
    <span className={`text-sm ${bold ? '' : 'text-muted-foreground'}`}>{label}</span>
    <span className={`font-mono text-sm inline-flex items-center gap-0.5 ${amount > 0 ? 'text-success' : amount < 0 ? 'text-error' : 'text-muted-foreground'}`}>
      {amount > 0 ? '+' : ''}<RiyalIcon size="0.85em" /> {amount.toLocaleString()}
    </span>
  </div>
);

export default function CashFlowPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(toLocalYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [endDate, setEndDate] = useState(localToday());
  const [method, setMethod] = useState('direct');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/accounting/cash-flow?start_date=${startDate}&end_date=${endDate}&method=${method}`);
      setData(res.data);
    } catch { toast.error('Failed to load cash flow'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [method, startDate, endDate]);

  const NetBadge = ({ value }) => {
    const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
    const color = value > 0 ? 'text-success bg-success/10' : value < 0 ? 'text-error bg-error/10' : 'text-muted-foreground bg-muted/50';
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-bold ${color}`}><Icon size={14} />{value.toLocaleString()}</span>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="cash-flow-page">
        <ReportHeader icon={Banknote} title={t('cash_flow_title') || 'Cash Flow Statement'} subtitle="Track cash inflows and outflows · company statement (all branches)" testId="cf-header" />
        <div className="flex flex-wrap items-center gap-2" data-testid="cf-filter">
            {/* Direct/Indirect method toggle */}
            <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="cf-method-toggle">
              <button
                onClick={() => setMethod('direct')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${method === 'direct' ? 'bg-primary text-white' : 'bg-background hover:bg-muted'}`}
                data-testid="cf-method-direct"
              >
                <ArrowUp size={11} className="inline mr-1 -mt-0.5" />
                Direct
              </button>
              <button
                onClick={() => setMethod('indirect')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${method === 'indirect' ? 'bg-primary text-white' : 'bg-background hover:bg-muted'}`}
                data-testid="cf-method-indirect"
              >
                <GitCompare size={11} className="inline mr-1 -mt-0.5" />
                Indirect
              </button>
            </div>
            <DateQuickFilter hideAll defaultActive="month" onFilterChange={(r) => { if (r?.start && r?.end) { setStartDate(r.start); setEndDate(r.end); } }} />
            <span className="text-[11px] font-mono text-muted-foreground" data-testid="cf-range">{startDate} → {endDate}</span>
            <span className="text-[10px] text-muted-foreground" title="Cash flow is a company-level statement">All branches (company statement)</span>
            <ExportButtons dataType="cash_flow" filters={{ method }} startDate={startDate} endDate={endDate} testid="cf-export" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-success border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <>
            <GlAccountingStrip mode="cash" startDate={startDate} endDate={endDate} operational={data.net_change_in_cash} operationalLabel="Operational net change" />
            {/* Net Change Summary */}
            <Card className={`border-2 card-enter ${data.net_change_in_cash >= 0 ? 'border-success/30 bg-gradient-to-r from-emerald-50 to-teal-50' : 'border-error/30 bg-gradient-to-r from-red-50 to-rose-50'}`}>
              <CardContent className="p-6 text-center">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Net Change in Cash</p>
                <p className={`text-4xl font-bold inline-flex items-center justify-center gap-1 ${data.net_change_in_cash >= 0 ? 'text-success' : 'text-error'}`} data-testid="cf-net-change">
                  <RiyalIcon size="0.65em" /> {data.net_change_in_cash.toLocaleString()}
                </p>
              </CardContent>
            </Card>

            {/* Indirect method reconciliation — only visible when method=indirect */}
            {data.operating_indirect && (
              <Card className="border-info/30" data-testid="cf-indirect-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <GitCompare size={16} className="text-info" />
                    Indirect Method Reconciliation
                    <span className="ml-auto"><NetBadge value={data.operating_indirect.net_operating} /></span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FlowRow label="Net Income (Revenue − Expenses)" amount={data.operating_indirect.net_income} indent />
                  <FlowRow label="+ Depreciation (non-cash)" amount={data.operating_indirect.adjustments.depreciation} indent />
                  <FlowRow label="− Increase in Receivables" amount={data.operating_indirect.adjustments.increase_in_receivables} indent />
                  <FlowRow label="+ Increase in Payables" amount={data.operating_indirect.adjustments.increase_in_payables} indent />
                  <FlowRow label="Net Cash from Operations (Indirect)" amount={data.operating_indirect.net_operating} bold />
                  <p className="text-[10px] text-muted-foreground italic mt-2 leading-relaxed">
                    {data.operating_indirect.note}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Operating */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowDown size={16} className="text-info" />
                  Operating Activities
                  <span className="ml-auto"><NetBadge value={data.operating.net} /></span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                <FlowRow label="Cash received from customers" amount={data.operating.cash_from_customers} indent />
                <FlowRow label="Credit sales (not yet collected)" amount={-data.operating.credit_sales_not_collected} indent />
                <FlowRow label="Expenses paid (cash/bank)" amount={data.operating.expenses_paid} indent />
                <FlowRow label="Supplier payments" amount={data.operating.supplier_payments} indent />
                <FlowRow label="Salary payments" amount={data.operating.salary_payments} indent />
                <FlowRow label="Net Cash from Operations" amount={data.operating.net} bold />
              </CardContent>
            </Card>

            {/* Investing */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building size={16} className="text-purple-500" />
                  Investing Activities
                  <span className="ml-auto"><NetBadge value={data.investing.net} /></span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FlowRow label="Asset purchases" amount={data.investing.asset_purchases} indent />
                <FlowRow label="Net Cash from Investing" amount={data.investing.net} bold />
              </CardContent>
            </Card>

            {/* Financing */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Handshake size={16} className="text-warning" />
                  Financing Activities
                  <span className="ml-auto"><NetBadge value={data.financing.net} /></span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FlowRow label="Partner investments" amount={data.financing.partner_investments} indent />
                <FlowRow label="Partner withdrawals" amount={data.financing.partner_withdrawals} indent />
                <FlowRow label="Loans received" amount={data.financing.loan_received} indent />
                <FlowRow label="Loan repayments" amount={data.financing.loan_repayments} indent />
                <FlowRow label="Net Cash from Financing" amount={data.financing.net} bold />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
